import type {
  CandidateRow,
  MappingReferenceType,
} from '@shared/models/element-import.model';
import type { Baseline } from '@shared/models/project.model';
import { getImportRowDraft } from '@shared/helpers/import-row-draft.helpers';
import {
  resolveMappedRowProductId,
  resolveMappedRowRecipeId,
  type ImportedElementsByRowId,
} from './candidate-status.helpers';
import type { MappingsByCode } from './element-imports.store';

/** Product/recipe reference copied from an import row for paste onto others. */
export type CopiedImportMapping = {
  referenceId: string;
  referenceType: MappingReferenceType;
};

type ResolveMappingReferenceOptions = {
  version: Baseline | undefined;
  mappingsByCode: MappingsByCode;
  importedElements: ImportedElementsByRowId;
};

/**
 * Resolve the climate-data mapping (product or recipe) currently chosen on a
 * candidate row — draft first, then a materialized element, then the org
 * resource-code mapping.
 */
export const resolveCandidateRowMappingReference = (
  row: CandidateRow,
  options: ResolveMappingReferenceOptions,
): CopiedImportMapping | undefined => {
  const draft = getImportRowDraft(options.version, row.id);
  if (draft.referenceId) {
    return {
      referenceId: draft.referenceId,
      referenceType: draft.referenceType ?? 'product',
    };
  }

  const element = options.importedElements.get(row.id);
  if (element) {
    const recipeId = resolveMappedRowRecipeId(
      element,
      row,
      options.mappingsByCode,
    );
    if (recipeId) {
      return { referenceId: recipeId, referenceType: 'recipe' };
    }
    const productId = resolveMappedRowProductId(
      element,
      row,
      options.mappingsByCode,
    );
    if (productId) {
      return { referenceId: productId, referenceType: 'product' };
    }
  }

  if (!row.resourceCode) return undefined;
  const stored = options.mappingsByCode[row.resourceCode];
  // Discard-only org rows have no climate target; a reference may still sit
  // alongside discard (auto-skip + stash) and is copyable.
  if (!stored?.referenceId) return undefined;
  return {
    referenceId: stored.referenceId,
    referenceType: stored.referenceType ?? 'product',
  };
};

/** Find a candidate row by id across import files. */
export const findCandidateRowById = (
  rowsByImport: Record<string, CandidateRow[]>,
  rowId: string,
): CandidateRow | undefined => {
  for (const rows of Object.values(rowsByImport)) {
    const row = rows.find((candidate) => candidate.id === rowId);
    if (row) return row;
  }
  return undefined;
};

/** Selected candidate rows in stable import→row order. Hidden ids are dropped. */
export const collectSelectedCandidateRows = (
  rowsByImport: Record<string, CandidateRow[]>,
  selectedIds: ReadonlySet<string>,
  visibleRowIds: ReadonlySet<string>,
): CandidateRow[] => {
  const out: CandidateRow[] = [];
  for (const rows of Object.values(rowsByImport)) {
    for (const row of rows) {
      if (selectedIds.has(row.id) && visibleRowIds.has(row.id)) out.push(row);
    }
  }
  return out;
};
